import Image from '@/models/image';
import IngredientGroup from '@/models/ingredientGroup';
import Product from '@/models/product';
import StorehouseUnitInterface from '@/models/storehouseUnitInterface';
import stringHelper from '@/utils/stringHelper';

export default class StorehouseProduct extends Product implements StorehouseUnitInterface {
  public count: number = 0;
  public maxRemainder: number = 0;
  public remainder: number = 0;

  public static createFromRequest(productProps: any): StorehouseProduct {
    const product = new StorehouseProduct();

    product.category = productProps.category;
    product.cost = productProps.cost;
    product.description = productProps.description;
    product.id = productProps.id;

    if (productProps.groups) {
      product.groups = productProps.groups
        .map(IngredientGroup.createByProps)
        .sort((firstGroup: IngredientGroup, secondGroup: IngredientGroup) => {
          return firstGroup.priority > secondGroup.priority ? 1 : -1;
        });
    }

    if (productProps.image) {
      product.image = new Image(productProps.image);
    }

    product.isFavorite = productProps.isFavorite;
    product.isShow = productProps.isShow;
    product.maxRemainder = productProps.maxRemainder || 100;
    product.priority = productProps.priority;
    product.remainder = productProps.remainder || 0;
    product.title = productProps.title;
    product.weight = productProps.weight;
    product.projectProduct = productProps.projectProduct;

    return product;
  }

  public get countPercent() {
    return (this.count > this.maxCount ? this.maxCount : this.count) * 100 / this.maxRemainder;
  }

  public get writeOffCountPercent() {
    const writeOffCountPercent = (this.count > this.remainder ? this.remainder : this.count) * 100 / (this.remainder < this.maxRemainder ? this.maxRemainder : this.remainder);

    if (writeOffCountPercent < 0) {
      return 0;
    }

    if (writeOffCountPercent > 100) {
      return 100;
    }

    return writeOffCountPercent;
  }

  public get maxCount() {
    return this.maxRemainder - this.remainder;
  }

  public get remainderPercent() {
    const remainder = this.remainder < 0 ? 0 : this.remainder;

    if (remainder > this.maxRemainder) {
      return 100;
    }

    return remainder * 100 / this.maxRemainder;
  }

  public get isSuccess() {
    return !this.isDanger && !this.isWarning;
  }

  public get isWarning() {
    return !this.isDanger && (this.remainder < this.maxRemainder / 2);
  }

  public get isDanger() {
    return this.remainder < this.maxRemainder / 4;
  }

  public isEqualSearchText(searchText: string = ''): boolean {
    searchText = searchText.toLocaleLowerCase();

    if (searchText.length === 0) {
      return true;
    }

    return this.title.toLocaleLowerCase().includes(searchText);
  }

  public compareName(storehouseUnit: StorehouseUnitInterface): number {
    return stringHelper.sortStrings(this.getName(), storehouseUnit.getName());
  }

  public compareCount(storehouseUnit: StorehouseUnitInterface): number {
    return this.getRemainder() > storehouseUnit.getRemainder() ? -1 : 1;
  }

  public getName(): string {
    return this.title;
  }

  public getRemainder(): number {
    return this.remainder;
  }

  public getRemainderDeviation(): number {
    const minNormal = this.maxRemainder / 4;

    if (this.remainder > this.maxRemainder) {
      const remainderDeviation = this.remainder - this.maxRemainder;

      if (remainderDeviation > minNormal) {
        return remainderDeviation - minNormal;
      }
    }

    if (this.remainder < minNormal) {
      return minNormal - this.remainder;
    }

    return 0;
  }
}
